import type { CreateSchoolDto, SchoolSearchResponseDto } from '@/castapi';
import { AppLogger } from '@/logger';
import type { ISearchParams } from '@/castapi/helpers';
import { getSchoolsApi, getErrorMessage } from '@/castapi/helpers';
import type { IActionParams } from '@/store/modules/index';
import type { ITableParamsState } from '@/store/modules/common/tableParamsState';
import TableParamsState from '@/store/modules/common/tableParamsState';

const logger = new AppLogger('adminSchools state');

const tableParams = new TableParamsState();

interface IAdminSchoolsState extends ITableParamsState {
  schools: SchoolSearchResponseDto[];
  schoolsLoading: boolean;
  schoolsLoadError?: string;
  schoolSaving: boolean;
  schoolSaveError?: string;
}

const initialState = (): IAdminSchoolsState => ({
  ...tableParams.state(),
  schools: [],
  schoolsLoading: false,
  schoolsLoadError: undefined,
  schoolSaving: false,
  schoolSaveError: undefined,
});

type ActionParams = IActionParams<IAdminSchoolsState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    ...tableParams.mutations,
    SCHOOLS_LOADING(state: IAdminSchoolsState): void {
      state.schoolsLoadError = undefined;
      state.schoolsLoading = true;
    },
    SCHOOLS_LOADED(state: IAdminSchoolsState, payload: SchoolSearchResponseDto[]): void {
      state.schools = payload;
      state.schoolsLoading = false;
    },
    SCHOOLS_LOAD_ERROR(state: IAdminSchoolsState, error: Error): void {
      state.schoolsLoadError = getErrorMessage(error);
      state.schoolsLoading = false;
    },
    SCHOOL_SAVING(state: IAdminSchoolsState): void {
      state.schoolSaveError = undefined;
      state.schoolSaving = true;
    },
    SCHOOL_SAVED(state: IAdminSchoolsState): void {
      state.schoolSaveError = undefined;
      state.schoolSaving = false;
    },
    SCHOOL_SAVE_ERROR(state: IAdminSchoolsState, error: Error): void {
      state.schoolSaveError = getErrorMessage(error);
      state.schoolSaving = false;
    },
    RESET_STATE(state: IAdminSchoolsState): void {
      Object.assign(state, initialState());
    },
  },
  actions: {
    ...tableParams.actions,
    async loadSchools({ commit, rootGetters }: ActionParams, searchParams: ISearchParams): Promise<void> {
      const { limit, offset, searchText, sortBy, sortDesc } = searchParams || {};
      commit('SCHOOLS_LOADING');
      try {
        const response = await getSchoolsApi(rootGetters['login/accessToken']).schoolsControllerGetSchools(
          limit,
          offset,
          searchText,
          sortBy,
          sortDesc,
        );
        commit('SCHOOLS_LOADED', response.data);
      } catch (error) {
        commit('SCHOOLS_LOAD_ERROR', error);
        logger.captureStoreError('loadSchools', error, { limit, offset, searchText, sortBy, sortDesc });
      }
    },
    async addSchool({ commit, rootGetters }: ActionParams, data: CreateSchoolDto): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      commit('SCHOOL_SAVING');
      try {
        await getSchoolsApi(accessToken).schoolsControllerCreate(data);
        commit('SCHOOL_SAVED');
      } catch (error) {
        commit('SCHOOL_SAVE_ERROR', error);
        logger.captureStoreError('addSchool', error, data);
      }
    },
    async updateSchool(
      { commit, rootGetters }: ActionParams,
      data: { schoolId: number; name?: string; domain?: string; countryId?: number; isPublished?: number },
    ): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      commit('SCHOOL_SAVING');
      try {
        await getSchoolsApi(accessToken).schoolsControllerUpdate(data.schoolId, {
          name: data.name,
          domain: data.domain,
          countryId: data.countryId,
          isPublished: data.isPublished,
        });
        commit('SCHOOL_SAVED');
      } catch (error) {
        commit('SCHOOL_SAVE_ERROR', error);
        logger.captureStoreError('updateSchool', error, data);
      }
    },
    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
  },
  getters: {
    ...tableParams.getters,
    schools: (state: IAdminSchoolsState): SchoolSearchResponseDto[] => state.schools,
    schoolsLoading: (state: IAdminSchoolsState): boolean => state.schoolsLoading,
    schoolsLoadError: (state: IAdminSchoolsState): undefined | string => state.schoolsLoadError,
    schoolSaving: (state: IAdminSchoolsState): boolean => state.schoolSaving,
    schoolSaveError: (state: IAdminSchoolsState): undefined | string => state.schoolSaveError,
  },
};
